/**
 * ------ maintenance history ------
 * 05/28/2019   Yoyo Fang   add single object configutation, add ability to reinit wrapped <research-screen> component
 * 05/29/2019   Yoyo Fang   differenciate layout according to embedMode
 * Updated by Abner Sui on 06/14/2019
 * Add route logic for email digest link
 * 08/02/2019   Yoyo Fang   change init logic of abondoned workflow: load existing specific research screen
 */
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { createSelector, Store, select } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take, catchError, skip } from 'rxjs/operators';
import { AppState, smartSelector } from '../../../redux';
import { fullEntityTypeSelector } from '../../../redux/reducers/entity-type.reducer';
import { fullEntryTypeSelector } from '../../../redux/reducers/entry-type.reducer';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { ResearchLayoutInfo, SortInfo, ResearchScreenInitializationPayload, DisplayInfo } from '../research.view-model';
import { ResearchFocusStatus, FinderSetting } from '../../../components/finder/finder.view-model';
import { ResearchLayoutMode } from '../../../components/research-layout/research-layout.data';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { RelationshipTreeService } from '../../../services/relationship-tree.service';
import { ScreenActionTypes } from '../../../redux/actions/screen.actions';
import {
    ResearchScreenInitializeLayout, ResearchScreenSetSortInfo, ResearchScreenSetFilterList,
    ResearchScreenSetFilterEnable, ResearchScreenSetFilterLogic, ResearchScreenSetKeyWord,
    ResearchScreenInitializeFocus, ResearchScreenSetRelationship, ResearchScreenThreadListSetGridStatus,
    ResearchScreenInitialization, ResearchScreenRefinebySetCategory, ResearchScreenSetDisplayInfo
} from '../../../redux/actions/research-screen.actions';
import { Entity } from '../../../tamalelibs/models/entity.model';
import { systemCurrentContactSelector } from '../../../redux/reducers/system.reducer';
import { ALL_VALUE, CURRENT_USER } from '../../../tamalelibs/constants/business.constants';
import { adhocSelector } from '../../../redux/reducers/adhoc.reducer';
import { ResearchScreenConfigs, ResearchScreenConfigAction, ResearchScreenConfigActionType } from '../../../components/research-screen/research-screen.model';
import { businessConstants } from '../../../constants/business.constants';
import { EmailDigestService } from '../../../services/email-digest.service';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { EmailSection } from '../../../tamalelibs/models/emaildigest.model';
import { ResearchViewConstants } from '../../../tamalelibs/models/smart-reducer.model';
import { validRelationshipTypeSelector } from '../../../redux/reducers/valid-relationship-type.reducer';
import { PrintService } from '../../../services/print.service';
import { ResearchHelperService } from '../../../services/research-helper.service';

@Component({
    selector: 'tam-research-wrapper',
    templateUrl: './research-wrapper.component.html',
    styleUrls: ['./research-wrapper.component.scss']
})
export class ResearchWrapperComponent implements OnInit, OnDestroy {
    @Input() config: ResearchScreenConfigs;
    keyOfScreen: string;
    isPageLoading = true;
    subscriptions: Subscription[] = [];

    constructor(
        private _entityService: EntityService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _emailDigestService: EmailDigestService,
        private _toast: ToastService,
        private _printService: PrintService,
        private _researchHelper: ResearchHelperService,
    ) { }

    ngOnInit() {
        this.init();
        this.subscriptions.push(
            this.config.actionSubject$.subscribe((action: ResearchScreenConfigAction) => {
                // when cleanup reload the researchscreen
                if (action.type === ResearchScreenConfigActionType.Reinitialize) {
                    this.config.searchText = action.payload.searchText;
                    this.isPageLoading = true;
                    this._store.dispatch({
                        type: businessConstants.research.deleteResearchStore,
                        prefix: this.keyOfScreen,
                    });
                    this.init();
                }
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription: Subscription) => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
        this._store.dispatch({
            type: businessConstants.research.deleteResearchStore,
            prefix: this.keyOfScreen,
        });
    }
    init() {
        if (!this.config) {
            this.config = new ResearchScreenConfigs(businessConstants.researchScreen.defaultResearchScreenKey);
            this.subscriptions.push(
                this._printService.actionSubject$.subscribe(action => this.config.actionSubject$.next(action)),

                this._researchHelper.researchActionSubject$.subscribe(action => this.config.actionSubject$.next(action)),
            );
        }
        this.keyOfScreen = this.config.screenKey;
        this._route.paramMap.pipe(
            skip(1)
        ).subscribe(() => this.initPage());

        this.initPage();
    }

    initPage() {
        this._store.dispatch({
            type: businessConstants.research.createResearchStore,
            prefix: this.keyOfScreen,
        });
        this.isPageLoading = true;
        const constantDataReadySelector = createSelector(
            fullEntityTypeSelector,
            fullEntryTypeSelector,
            adhocSelector,
            validRelationshipTypeSelector,
            (state: AppState) => state.relationshipType,
            (allEntityTypes, allEntryTypes, adhoc, validRelationshipType: any, allRelationshipTypes) => {
                if (allEntityTypes && allEntityTypes.length > 0 &&
                    allEntryTypes && allEntryTypes.length > 0 &&
                    allRelationshipTypes && allRelationshipTypes.length > 0 &&
                    adhoc && adhoc.db && adhoc.title &&
                    validRelationshipType && validRelationshipType.length > 0) {
                    return true;
                } else {
                    return false;
                }
            }
        );
        this.subscriptions.push(
            this._store.pipe(
                select(constantDataReadySelector),
                filter(isReady => isReady),
                switchMap(() => {
                    return this._initializeStoreAgainstDifferentEntrance();
                }),
                take(1),
            ).subscribe(() => {
                this.isPageLoading = false;
            })
        );
    }

    private _initializeStoreAgainstDifferentEntrance(): Observable<any> {
        if (this._route.snapshot.paramMap.has(businessConstants.research.researchKey)) {
            const id = this._route.snapshot.paramMap.get(businessConstants.research.researchKey);
            const user = this._storeQuerier.getCurrentUser();
            const page = {
                screenId: id,
                configuration: null,
                withSubChrome: true
            };
            this._store.dispatch(
                new ResearchScreenInitialization(
                    this.keyOfScreen,
                    new ResearchScreenInitializationPayload(page, user)
                )
            );
            // branch abandoned, otherwise this.config should be set here,
            // so that refineBy and other inner components don't get error on selecting store data from undefined.
            return this._store.select(smartSelector(this.keyOfScreen, ResearchViewConstants.ReducerName)).pipe(
                filter((state) => state && state.loading < 1),
                take(1),
                map((config) => {
                    if (config.loading === 0) {
                        this.config = new ResearchScreenConfigs(this.keyOfScreen, false, config.keywordInfo);
                    } else if (config.loading < 0) {
                        this._router.navigate([businessConstants.common.navigateToErrorPage]);
                    }
                })
            );
        } else if (this._route.snapshot.queryParams
            && this._route.snapshot.queryParams.digestid
            && this._route.snapshot.queryParams.sectionid) {
            const digestId = this._route.snapshot.queryParams.digestid;
            const sectionId = this._route.snapshot.queryParams.sectionid;
            return this._emailDigestService.getEmailDigestSection(digestId, sectionId).pipe(
                catchError((e) => {
                    this._toast.notify({
                        style: NotificationStyles.Error,
                        message: e.message,
                    });
                    this._router.navigate([businessConstants.common.navigateToErrorPage]);
                    return of(null);
                }),
                take(1),
                map((res: { name: string, emailSection: EmailSection }) => {
                    let tempAdvFilter = res.emailSection.advFilter;
                    const userId = this._storeQuerier.getCurrentUser().id;
                    while (tempAdvFilter.indexOf(CURRENT_USER.id) > -1) {
                        tempAdvFilter = tempAdvFilter.replace(CURRENT_USER.id, userId);
                    }
                    this.config.advFilter = tempAdvFilter;
                    const currentPageInfo: any = {
                        url: this._route.snapshot.url,
                        name: businessConstants.homeScreen.newResearchTabName,
                        // type: businessConstants.research.pageTypeText, // email digest link should not set page type as research page
                    };
                    this._store.dispatch({
                        type: ScreenActionTypes.SET_VALUE,
                        payload: currentPageInfo,
                    });

                    // entrance for default research view
                    const finderSetting: FinderSetting = {
                        entity: this._storeQuerier.queryBySelector(systemCurrentContactSelector),
                        status: ResearchFocusStatus.ALL,
                    };

                    this._store.dispatch(new ResearchScreenInitializeFocus(this.keyOfScreen, finderSetting));
                    const layout = this.config.embedMode ? ResearchLayoutMode.SIDE_LIST : ResearchLayoutMode.SNIPPET;
                    this._store.dispatch(new ResearchScreenInitializeLayout(this.keyOfScreen, new ResearchLayoutInfo(layout, this.config.embedMode, this.config.embedMode)));
                    this._store.dispatch(new ResearchScreenSetSortInfo(this.keyOfScreen, new SortInfo()));
                    this._store.dispatch(new ResearchScreenSetFilterList(this.keyOfScreen, []));
                    this._store.dispatch(new ResearchScreenSetFilterEnable(this.keyOfScreen, false));
                    this._store.dispatch(new ResearchScreenSetFilterLogic(this.keyOfScreen, ALL_VALUE));
                    this._store.dispatch(new ResearchScreenSetKeyWord(this.keyOfScreen, this.config.searchText));
                    this._store.dispatch(new ResearchScreenThreadListSetGridStatus(this.keyOfScreen, ''));
                    return finderSetting;
                }),
                switchMap((finderSetting: FinderSetting) => {
                    return this._entityService.getEntityDetailWithoutAdhoc(finderSetting.entity.id).pipe(
                        map(response => {
                            const entity = Entity.parse(response);
                            const nodeData = RelationshipTreeService.parseToTreeViewModel(entity);
                            const expandedKeys = nodeData.filter(node => !node.isLeaf).map(node => node.id);
                            this._store.dispatch(new ResearchScreenSetRelationship(this.keyOfScreen, {
                                nodes: nodeData,
                                expandedKeys: expandedKeys,
                                checkedKeys: [],
                                showAs: businessConstants.entity.shortName,
                            }));
                        })
                    );
                }),
            );
        } else {
            const currentPageInfo: any = {
                url: this._route.snapshot.url,
                name: businessConstants.homeScreen.newResearchTabName,
                // type: businessConstants.research.pageTypeText,
            };
            // simple search page should not set page type as research page
            if (!this.config.searchText) {
                currentPageInfo.type = businessConstants.research.pageTypeText;
            }
            this._store.dispatch({
                type: ScreenActionTypes.SET_VALUE,
                payload: currentPageInfo,
            });
            // entrance for default research view
            const finderSetting: FinderSetting = {
                entity: this._storeQuerier.queryBySelector(systemCurrentContactSelector),
                status: ResearchFocusStatus.ALL,
            };

            this._store.dispatch(new ResearchScreenInitializeFocus(this.keyOfScreen, finderSetting));
            const layout = this.config.embedMode ? ResearchLayoutMode.SIDE_LIST : ResearchLayoutMode.SNIPPET;
            this._store.dispatch(new ResearchScreenInitializeLayout(this.keyOfScreen, new ResearchLayoutInfo(layout, this.config.embedMode, this.config.embedMode)));
            this._store.dispatch(new ResearchScreenSetSortInfo(this.keyOfScreen, new SortInfo()));
            this._store.dispatch(new ResearchScreenSetFilterList(this.keyOfScreen, []));
            this._store.dispatch(new ResearchScreenSetFilterEnable(this.keyOfScreen, false));
            this._store.dispatch(new ResearchScreenSetFilterLogic(this.keyOfScreen, ALL_VALUE));
            this._store.dispatch(new ResearchScreenSetKeyWord(this.keyOfScreen, this.config.searchText));
            this._store.dispatch(new ResearchScreenThreadListSetGridStatus(this.keyOfScreen, ''));
            const displayInfo = new DisplayInfo();
            displayInfo.showCalendarEvents = this.config.showCalendarEvents;
            this._store.dispatch(new ResearchScreenSetDisplayInfo(this.keyOfScreen, displayInfo));
            if (this.config.refinebyCategory && this.config.refinebyCategory.length) {
                this._store.dispatch(new ResearchScreenRefinebySetCategory(this.keyOfScreen, this.config.refinebyCategory));
            }

            return this._entityService.getEntityDetailWithoutAdhoc(finderSetting.entity.id).pipe(
                map(response => {
                    const entity = Entity.parse(response);
                    const nodeData = RelationshipTreeService.parseToTreeViewModel(entity);
                    const expandedKeys = nodeData.filter(node => !node.isLeaf).map(node => node.id);
                    this._store.dispatch(new ResearchScreenSetRelationship(this.keyOfScreen, {
                        nodes: nodeData,
                        expandedKeys: expandedKeys,
                        checkedKeys: [],
                        showAs: businessConstants.entity.shortName,
                    }));
                })
            );
        }
    }
}
